import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { label: "首页", to: "/", type: "" },
  { label: "电影", to: "/category?type=电影", type: "电影" },
  { label: "电视剧", to: "/category?type=电视剧", type: "电视剧" },
  { label: "关于", to: "/about", type: "about" },
];

interface NavbarProps {
  overlay?: boolean;
}

export default function Navbar({ overlay = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const solid = !overlay || scrolled || menuOpen;

  const submitSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
    setMenuOpen(false);
  };

  const isActive = (type: string) => {
    if (type === "" ) return location.pathname === "/";
    if (type === "about") return location.pathname === "/about";
    if (location.pathname === "/category") {
      const params = new URLSearchParams(location.search);
      return params.get("type") === type;
    }
    return false;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-background-950/95 backdrop-blur border-b border-background-800"
          : "bg-gradient-to-b from-background-950/80 to-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-[72px] flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0 group">
          <span className="w-9 h-9 rounded-md bg-primary-600 text-background-50 flex items-center justify-center text-lg font-bold">
            <i className="ri-movie-2-line"></i>
          </span>
          <span className="font-heading text-lg md:text-xl font-black tracking-wide text-foreground-50 whitespace-nowrap">
            岳母的诱惑
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                isActive(link.type)
                  ? "text-accent-400 bg-background-800/70"
                  : "text-foreground-200 hover:text-accent-400 hover:bg-background-800/50"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="hidden md:flex items-center ml-auto max-w-xs w-full bg-background-900 border border-background-700 rounded-full px-3 py-1.5 focus-within:border-primary-500 transition-colors"
        >
          <i className="ri-search-line text-foreground-400 text-base w-5 h-5 flex items-center justify-center"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索影片、类型、地区"
            className="flex-1 bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 outline-none px-2"
          />
          <button
            type="submit"
            className="text-xs text-accent-400 font-medium whitespace-nowrap hover:text-accent-300 cursor-pointer"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开菜单"
          className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md text-foreground-100 bg-background-900 border border-background-800 cursor-pointer"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-950/98 backdrop-blur border-t border-background-800 px-4 py-4 animate-fade-in">
          <form
            onSubmit={submitSearch}
            className="flex items-center bg-background-900 border border-background-700 rounded-full px-3 py-2 mb-3"
          >
            <i className="ri-search-line text-foreground-400 text-base w-5 h-5 flex items-center justify-center"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、类型、地区"
              className="flex-1 bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 outline-none px-2"
            />
          </form>
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="px-3 py-3 rounded-md text-sm font-medium text-foreground-200 hover:bg-background-900 hover:text-accent-400"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}